import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { followAPI } from '../services/api';
import { profileThumb, profileThumbFallback } from '../utils/imageHelpers';
import PageHeader from '../components/PageHeader';
import { 
    Heart, Users, Calendar, Music, Video, ImageIcon, 
    Play, Eye, MessageSquare, ThumbsUp, Clock, 
    UserMinus, Search, Filter, Grid, List, Loader,
    Star, Verified, MapPin, ExternalLink, AlertCircle,
    TrendingUp, Activity, Volume2
} from 'lucide-react';

const Following = () => {
    const { user, isAuthenticated } = useAuth();
    const [followingData, setFollowingData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('recent'); // recent, alphabetical, most_active
    const [filterBy, setFilterBy] = useState('all'); // all, artists, verified, crew
    const [viewMode, setViewMode] = useState('grid'); // grid, list
    const [recentActivity, setRecentActivity] = useState([]);
    const [stats, setStats] = useState({ totalFollowing: 0, activeToday: 0, newContent: 0 });

    useEffect(() => {
        if (isAuthenticated && user) {
            fetchFollowingData();
            fetchRecentActivity();
        }
    }, [isAuthenticated, user]);

    const fetchFollowingData = async () => {
        try {
            setLoading(true);
            const response = await followAPI.getFollowing(user.id);
            if (response.data.success) {
                setFollowingData(response.data.following || []);
                setStats({
                    totalFollowing: response.data.following?.length || 0,
                    activeToday: response.data.activeToday || 0,
                    newContent: response.data.newContent || 0
                });
            }
        } catch (error) {
            console.error('Error fetching following:', error);
            setError('Failed to load following list');
        } finally {
            setLoading(false);
        }
    };

    const fetchRecentActivity = async () => {
        try {
            const response = await followAPI.getFollowingActivity(user.id);
            if (response.data.success) {
                setRecentActivity(response.data.activity || []);
            }
        } catch (error) {
            console.error('Error fetching activity:', error);
        }
    };

    const handleUnfollow = async (userId, username) => {
        if (!window.confirm(`Unfollow ${username}?`)) return;
        
        try {
            await followAPI.unfollowUser(userId);
            setFollowingData(prev => prev.filter(user => user.user_id !== userId));
            setStats(prev => ({ ...prev, totalFollowing: prev.totalFollowing - 1 }));
        } catch (error) {
            console.error('Error unfollowing user:', error);
            alert('Failed to unfollow user');
        }
    };

    // Filter and sort following data
    const filteredAndSortedData = React.useMemo(() => {
        let filtered = followingData.filter(user => {
            // Search filter
            const matchesSearch = searchQuery === '' || 
                user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
                user.display_name.toLowerCase().includes(searchQuery.toLowerCase());

            // Category filter
            const matchesFilter = filterBy === 'all' || 
                (filterBy === 'artists' && user.is_artist) ||
                (filterBy === 'verified' && user.artist_verified) ||
                (filterBy === 'crew' && user.is_crew_member);

            return matchesSearch && matchesFilter;
        });

        // Sort
        filtered.sort((a, b) => {
            switch (sortBy) {
                case 'alphabetical':
                    return a.display_name.localeCompare(b.display_name);
                case 'most_active':
                    const aActivity = (a.recent_media?.length || 0) + (a.follower_count || 0);
                    const bActivity = (b.recent_media?.length || 0) + (b.follower_count || 0);
                    return bActivity - aActivity;
                case 'recent':
                default:
                    return new Date(b.followed_at) - new Date(a.followed_at);
            }
        });

        return filtered;
    }, [followingData, searchQuery, sortBy, filterBy]);

    const formatTimeAgo = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
        
        if (diffInHours < 1) return 'Just now';
        if (diffInHours < 24) return `${diffInHours}h ago`;
        if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`;
        return `${Math.floor(diffInHours / 168)}w ago`;
    };

    const getMediaIcon = (type) => {
        switch (type) {
            case 'audio': return <Music className="w-3 h-3" />;
            case 'video': return <Video className="w-3 h-3" />;
            case 'image': return <ImageIcon className="w-3 h-3" />;
            default: return <Music className="w-3 h-3" />;
        }
    };

    const getActivityIcon = (activity) => {
        if (activity.activity_type === 'post') return <MessageSquare className="w-3 h-3" />;
        return getMediaIcon(activity.content_type);
    };

    const getActivityLink = (activity) => {
        if (activity.activity_type === 'post') {
            return `/view/post/${activity.username}/${activity.content_id}`;
        }
        const filename = activity.stored_filename || activity.original_filename || activity.content_id;
        return `/view/${activity.content_type}/${activity.username}/${filename}`;
    };

    if (!isAuthenticated) {
        return (
            <div className="min-h-screen bg-gray-900 pt-20 pb-12">
                <div className="max-w-4xl mx-auto px-6">
                    <div className="glass-card p-8 text-center">
                        <Heart className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                        <h1 className="text-2xl font-bold text-white mb-4">Sign In Required</h1>
                        <p className="text-gray-400 mb-6">
                            Please sign in to view your following list and discover content from creators you follow.
                        </p>
                        <Link to="/login" className="btn-primary">
                            Sign In
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900">
            <PageHeader 
                title="Following"
                subtitle="Stay connected with your favorite creators"
                backgroundImage="/uploads/site-images/following-header.jpg"
            />
            
            <div className="max-w-7xl mx-auto px-6 py-8">
                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="glass-card p-6">
                        <div className="flex items-center gap-4">
                            <Users className="w-8 h-8 text-blue-500" />
                            <div>
                                <p className="text-2xl font-bold text-white">{stats.totalFollowing}</p>
                                <p className="text-gray-400 text-sm">Following</p>
                            </div>
                        </div>
                    </div>
                    
                    <div className="glass-card p-6">
                        <div className="flex items-center gap-4">
                            <Activity className="w-8 h-8 text-green-500" />
                            <div>
                                <p className="text-2xl font-bold text-white">{stats.activeToday}</p>
                                <p className="text-gray-400 text-sm">Active Today</p>
                            </div>
                        </div>
                    </div>
                    
                    <div className="glass-card p-6">
                        <div className="flex items-center gap-4">
                            <TrendingUp className="w-8 h-8 text-purple-500" />
                            <div>
                                <p className="text-2xl font-bold text-white">{stats.newContent}</p>
                                <p className="text-gray-400 text-sm">New Content</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
                    {/* Main Following List */}
                    <div className="xl:col-span-3">
                        <div className="glass-card p-6">
                            {/* Controls */}
                            <div className="flex flex-col sm:flex-row gap-4 mb-6">
                                <div className="flex-1">
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                        <input
                                            type="text"
                                            placeholder="Search following..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        />
                                    </div>
                                </div>
                                
                                <div className="flex gap-2">
                                    <select
                                        value={sortBy}
                                        onChange={(e) => setSortBy(e.target.value)}
                                        className="px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="recent">Recently Followed</option>
                                        <option value="alphabetical">Alphabetical</option>
                                        <option value="most_active">Most Active</option>
                                    </select>
                                    
                                    <select
                                        value={filterBy}
                                        onChange={(e) => setFilterBy(e.target.value)}
                                        className="px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="all">All Users</option>
                                        <option value="artists">Artists</option>
                                        <option value="verified">Verified</option>
                                        <option value="crew">Crew Members</option>
                                    </select>
                                    
                                    <div className="flex bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
                                        <button
                                            onClick={() => setViewMode('grid')}
                                            className={`p-2 transition-colors ${viewMode === 'grid' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'}`}
                                        >
                                            <Grid className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => setViewMode('list')}
                                            className={`p-2 transition-colors ${viewMode === 'list' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'}`}
                                        >
                                            <List className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Following List */}
                            {loading ? (
                                <div className="text-center py-12">
                                    <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-500" />
                                    <p className="text-gray-400">Loading following list...</p>
                                </div>
                            ) : error ? (
                                <div className="text-center py-12">
                                    <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                                    <p className="text-red-400 mb-4">{error}</p>
                                    <button onClick={fetchFollowingData} className="btn-secondary">
                                        Try Again
                                    </button>
                                </div>
                            ) : filteredAndSortedData.length === 0 ? (
                                <div className="text-center py-12">
                                    <Heart className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                                    <h3 className="text-xl font-bold text-white mb-2">
                                        {searchQuery || filterBy !== 'all' ? 'No matches found' : 'No following yet'}
                                    </h3>
                                    <p className="text-gray-400 mb-6">
                                        {searchQuery || filterBy !== 'all' 
                                            ? 'Try adjusting your search or filter settings'
                                            : 'Start following creators to see their latest content here'
                                        }
                                    </p>
                                    {!searchQuery && filterBy === 'all' && (
                                        <Link to="/community" className="btn-primary">
                                            Discover Creators
                                        </Link>
                                    )}
                                </div>
                            ) : (
                                <div className={`grid gap-6 ${
                                    viewMode === 'grid' 
                                        ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' 
                                        : 'grid-cols-1'
                                }`}>
                                    {filteredAndSortedData.map((followedUser) => (
                                        <div key={followedUser.user_id} className={`group relative ${
                                            viewMode === 'grid' 
                                                ? 'bg-gray-800/50 rounded-lg border border-gray-700 hover:border-gray-600 transition-all duration-300 p-4'
                                                : 'bg-gray-800/30 rounded-lg border border-gray-700 hover:border-gray-600 transition-all duration-300 p-4 flex items-center gap-4'
                                        }`}>
                                            
                                            {/* Profile Section */}
                                            <div className={`${viewMode === 'list' ? 'flex items-center gap-4 flex-1' : ''}`}>
                                                <div className="relative">
                                                    <Link to={`/profile/${followedUser.username}`}>
                                                        {followedUser.profile_picture_url ? (
                                                            <img
                                                                src={profileThumb(followedUser.profile_picture_url)}
                                                                alt={followedUser.display_name}
                                                                width="64"
                                                                height="64"
                                                                onError={profileThumbFallback(followedUser.profile_picture_url)}
                                                                className={`rounded-full object-cover border-2 border-gray-600 group-hover:border-purple-500 transition-colors ${
                                                                    viewMode === 'grid' ? 'w-16 h-16 mx-auto mb-3' : 'w-12 h-12'
                                                                }`}
                                                            />
                                                        ) : (
                                                            <div className={`rounded-full bg-gradient-to-br from-purple-600 to-pink-600 border-2 border-gray-600 group-hover:border-purple-500 transition-colors flex items-center justify-center ${
                                                                viewMode === 'grid' ? 'w-16 h-16 mx-auto mb-3' : 'w-12 h-12'
                                                            }`}>
                                                                <span className="text-white font-bold">
                                                                    {followedUser.display_name?.charAt(0).toUpperCase() || '🌊'}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </Link>
                                                    
                                                    {/* Status indicators */}
                                                    <div className="absolute -top-1 -right-1 flex gap-1">
                                                        {followedUser.artist_verified && (
                                                            <Verified className="w-4 h-4 text-blue-500" title="Verified Artist" />
                                                        )}
                                                        {followedUser.is_crew_member && (
                                                            <Star className="w-4 h-4 text-yellow-500" title="Crew Member" />
                                                        )}
                                                    </div>
                                                </div>
                                                
                                                <div className={`${viewMode === 'grid' ? 'text-center' : 'flex-1'}`}>
                                                    <Link to={`/profile/${followedUser.username}`}>
                                                        <h3 className="font-bold text-white group-hover:text-purple-400 transition-colors">
                                                            {followedUser.display_name}
                                                        </h3>
                                                        <p className="text-gray-400 text-sm">@{followedUser.username}</p>
                                                    </Link>
                                                    
                                                    {followedUser.crew_role && (
                                                        <p className="text-yellow-400 text-xs font-semibold mt-1">
                                                            {followedUser.crew_role}
                                                        </p>
                                                    )}
                                                    
                                                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                                                        <span className="flex items-center gap-1">
                                                            <Users className="w-3 h-3" />
                                                            {followedUser.follower_count || 0}
                                                        </span>
                                                        <span className="flex items-center gap-1">
                                                            <Clock className="w-3 h-3" />
                                                            {formatTimeAgo(followedUser.followed_at)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Recent Media Preview */}
                                            {followedUser.recent_media && followedUser.recent_media.length > 0 && (
                                                <div className={`${viewMode === 'grid' ? 'mt-4' : 'flex-shrink-0'}`}>
                                                    <h4 className="text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wide">
                                                        Recent
                                                    </h4>
                                                    <div className={`${viewMode === 'grid' ? 'space-y-2' : 'flex gap-2'}`}>
                                                        {followedUser.recent_media.slice(0, 2).map((media, index) => (
                                                            <Link
                                                                key={index}
                                                                to={`/view/${media.type}/${followedUser.username}/${media.stored_filename}`}
                                                                className="group/media flex items-center gap-2 p-2 bg-gray-900/50 rounded hover:bg-gray-900 transition-colors"
                                                            >
                                                                {getMediaIcon(media.type)}
                                                                <div className="flex-1 min-w-0">
                                                                    <p className="text-xs text-white truncate group-hover/media:text-purple-400">
                                                                        {media.title || media.original_filename}
                                                                    </p>
                                                                    <div className="flex items-center gap-2 text-xs text-gray-500">
                                                                        <span className="flex items-center gap-1">
                                                                            <Play className="w-2 h-2" />
                                                                            {media.play_count || 0}
                                                                        </span>
                                                                        <span>{formatTimeAgo(media.created_at)}</span>
                                                                    </div>
                                                                </div>
                                                            </Link>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Action Buttons */}
                                            <div className={`${viewMode === 'grid' ? 'mt-4 pt-4 border-t border-gray-700' : 'flex-shrink-0'}`}>
                                                <div className="flex gap-2">
                                                    <Link
                                                        to={`/profile/${followedUser.username}`}
                                                        className="flex-1 px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded text-xs text-center transition-colors"
                                                    >
                                                        View Profile
                                                    </Link>
                                                    <button
                                                        onClick={() => handleUnfollow(followedUser.user_id, followedUser.username)}
                                                        className="px-3 py-1 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded text-xs transition-colors flex items-center gap-1"
                                                        title="Unfollow"
                                                    >
                                                        <UserMinus className="w-3 h-3" />
                                                        {viewMode === 'list' && 'Unfollow'}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Sidebar - Recent Activity */}
                    <div className="xl:col-span-1">
                        <div className="glass-card p-6">
                            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                                <Volume2 className="w-5 h-5 text-purple-500" />
                                Recent Activity
                            </h3>
                            
                            {recentActivity.length === 0 ? (
                                <div className="text-center py-8">
                                    <Activity className="w-12 h-12 text-gray-500 mx-auto mb-3" />
                                    <p className="text-gray-400 text-sm">No recent activity</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {recentActivity.slice(0, 10).map((activity, index) => (
                                        <div key={index} className="flex items-start gap-3 p-3 bg-gray-800/50 rounded-lg">
                                            <Link to={`/profile/${activity.username}`} className="flex-shrink-0">
                                                {activity.profile_picture_url ? (
                                                    <img
                                                        src={profileThumb(activity.profile_picture_url)}
                                                        alt={activity.username}
                                                        width="32"
                                                        height="32"
                                                        onError={profileThumbFallback(activity.profile_picture_url)}
                                                        className="w-8 h-8 rounded-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center">
                                                        <span className="text-white text-xs font-bold">
                                                            {activity.username?.charAt(0).toUpperCase() || '🌊'}
                                                        </span>
                                                    </div>
                                                )}
                                            </Link>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm">
                                                    <Link
                                                        to={`/profile/${activity.username}`}
                                                        className="font-semibold text-white hover:text-purple-400"
                                                    >
                                                        {activity.display_name}
                                                    </Link>
                                                    <span className="text-gray-400"> {activity.action}</span>
                                                </p>
                                                {activity.content_title && (
                                                    <Link
                                                        to={getActivityLink(activity)}
                                                        className="flex items-center gap-1.5 text-xs text-purple-400 hover:text-purple-300 truncate mt-0.5"
                                                    >
                                                        {getActivityIcon(activity)}
                                                        <span className="truncate">{activity.content_title}</span>
                                                    </Link>
                                                )}
                                                <p className="text-xs text-gray-500 mt-0.5">{formatTimeAgo(activity.created_at)}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Quick Actions */}
                        <div className="glass-card p-6 mt-6">
                            <h3 className="text-lg font-bold text-white mb-4">Quick Actions</h3>
                            <div className="space-y-3">
                                <Link to="/community" className="block w-full btn-secondary text-center">
                                    <Users className="w-4 h-4 inline mr-2" />
                                    Discover Users
                                </Link>
                                <Link to="/feed" className="block w-full btn-secondary text-center">
                                    <TrendingUp className="w-4 h-4 inline mr-2" />
                                    Explore Feed
                                </Link>
                                <Link to="/gallery" className="block w-full btn-secondary text-center">
                                    <ImageIcon className="w-4 h-4 inline mr-2" />
                                    Browse Gallery
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Following;